import logger from '../utils/logger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Zap, Search, CreditCard, Menu } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import MobileNavigationDrawer from './MobileNavigationDrawer';
import NotificationSidebar from './NotificationSidebar';
import ChangelogSidebar, { GiftIcon } from './ChangelogSidebar';
import useChangelog, { isWhatsNewDue } from '../hooks/useChangelog';
import { CHANGELOG_SPOTLIGHT_DATE } from '../data/changelogLatest';
import { safeGetItem, safeSetItem } from '../utils/storage';
import { useLocation, useNavigate } from 'react-router-dom';
import useSocket from '../hooks/useSocket';
import axios from 'axios';
import { getApiBaseUrlWithApi, buildApiUrl } from '../config/api';
import UpgradeConfirmationModal from '@/components/billing/UpgradeConfirmationModal';
// BaseModal sur ordinateur, feuille du bas sur le téléphone du coach.
import SheetOrModal from './ui/modal/SheetOrModal';
import { lazyImport } from '../utils/lazyImport';

// Ouverts seulement depuis une notification : chargés à ce moment-là, pas
// dans le bloc principal (la modale vidéo coach et la vue vidéo élève pèsent
// lourd — lecteur, enregistreur, annotations).
const VideoDetailModal = lazyImport(() => import('./VideoDetailModal'));
const StudentVideoDetailOverlay = lazyImport(() => import('./student/videos/StudentVideoDetailOverlay'));
import { loadPushNamespace } from '../i18n/loadPushNamespace';

// System push settings: its own chunk, fetched on first open only, together
// with the lazy `push` i18n namespace it renders.
const NotificationSettingsSheet = lazyImport(() => Promise.all([
  import('./notifications/NotificationSettingsSheet'),
  loadPushNamespace(),
]).then(([m]) => m));

// Coach settings panel (header gear): its own chunk, fetched on first open.
const SettingsSidebar = lazyImport(() => import('./SettingsSidebar'));
// Pop-up « Nouveautés » après une mise à jour : chargée seulement quand elle s'affiche.
const WhatsNewModal = lazyImport(() => import('./WhatsNewModal'));

// Custom Notification Icon Component
const NotificationIcon = ({ className, style }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 640 640"
    className={className}
    style={style}
    fill="currentColor"
  >
    <path d="M320 64C302.3 64 288 78.3 288 96L288 99.2C215 114 160 178.6 160 256L160 277.7C160 325.8 143.6 372.5 113.6 410.1L103.8 422.3C98.7 428.6 96 436.4 96 444.5C96 464.1 111.9 480 131.5 480L508.4 480C528 480 543.9 464.1 543.9 444.5C543.9 436.4 541.2 428.6 536.1 422.3L526.3 410.1C496.4 372.5 480 325.8 480 277.7L480 256C480 178.6 425 114 352 99.2L352 96C352 78.3 337.7 64 320 64zM258 528C265.1 555.6 290.2 576 320 576C349.8 576 374.9 555.6 382 528L258 528z"/>
  </svg>
);

// Custom Settings Icon Component
const SettingsIcon = ({ className, style }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 640 640"
    className={className}
    style={style}
    fill="currentColor"
  >
    <path d="M259.1 73.5C262.1 58.7 275.2 48 290.4 48L350.2 48C365.4 48 378.5 58.7 381.5 73.5L396 143.5C410.1 149.5 423.3 157.2 435.3 166.3L503.1 143.8C517.5 139 533.3 145 540.9 158.2L570.8 210C578.4 223.2 575.7 239.8 564.3 249.9L511 297.3C511.9 304.7 512.3 312.3 512.3 320C512.3 327.7 511.8 335.3 511 342.7L564.4 390.2C575.8 400.3 578.4 417 570.9 430.1L541 481.9C533.4 495 517.6 501.1 503.2 496.3L435.4 473.8C423.3 482.9 410.1 490.5 396.1 496.6L381.7 566.5C378.6 581.4 365.5 592 350.4 592L290.6 592C275.4 592 262.3 581.3 259.3 566.5L244.9 496.6C230.8 490.6 217.7 482.9 205.6 473.8L137.5 496.3C123.1 501.1 107.3 495.1 99.7 481.9L69.8 430.1C62.2 416.9 64.9 400.3 76.3 390.2L129.7 342.7C128.8 335.3 128.4 327.7 128.4 320C128.4 312.3 128.9 304.7 129.7 297.3L76.3 249.8C64.9 239.7 62.3 223 69.8 209.9L99.7 158.1C107.3 144.9 123.1 138.9 137.5 143.7L205.3 166.2C217.4 157.1 230.6 149.5 244.6 143.4L259.1 73.5zM320.3 400C364.5 399.8 400.2 363.9 400 319.7C399.8 275.5 363.9 239.8 319.7 240C275.5 240.2 239.8 276.1 240 320.3C240.2 364.5 276.1 400.2 320.3 400z"/>
  </svg>
);

/**
 * App header bar.
 *
 * `hideBar` drops ONLY the visible `<header>` element; the overlays this
 * component owns (NotificationSidebar, MobileNavigationDrawer, the video detail
 * modal) stay mounted, and so do the notification fetch and socket
 * subscriptions. Used by the redesigned student screens (see
 * `STUDENT_OWN_HEADER_ROUTES` in MainLayout), whose Figma header replaces the
 * app bar — they reach the bell and the menu through `useHeaderActions()`.
 *
 * `registerActions` is how those openers get published: MainLayout hands them
 * to its HeaderActionsContext, so the state stays here (one sidebar, one fetch)
 * while any page under the layout can trigger it. Passed as a prop rather than
 * consumed from MainLayout's context because MainLayout imports this module —
 * importing back would create a cycle.
 *
 * The same registration also carries the notifications themselves and their
 * mutation callbacks, so the student bottom sheet
 * (<StudentNotificationsSheet>, mounted once by MainLayout) renders THIS fetch
 * rather than opening a second one. Nothing else may fetch
 * `/api/notifications`.
 */
const Header = ({ hideBar = false, registerActions }) => {
  const { user, logout, getAuthToken } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [hasOpenedSettings, setHasOpenedSettings] = useState(false);
  // Push settings sheet: mounted on first open, then kept so its exit animation plays.
  const [isPushSettingsOpen, setIsPushSettingsOpen] = useState(false);
  const [hasOpenedPushSettings, setHasOpenedPushSettings] = useState(false);
  const openPushSettings = useCallback(() => {
    setIsSettingsOpen(false);
    setIsMobileMenuOpen(false);
    setHasOpenedPushSettings(true);
    setIsPushSettingsOpen(true);
  }, []);
  const closePushSettings = useCallback(() => setIsPushSettingsOpen(false), []);
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [coachPlan, setCoachPlan] = useState('free');
  const [isCoachPlanLoaded, setIsCoachPlanLoaded] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const location = useLocation();
  const { onVideoUpload, onFeedback, onAccessChange, onStudentNotification, onCoachNotification } = useSocket();
  
  const {
    entries: changelogEntries,
    lastSeenDate: changelogLastSeenDate,
    hasUnseen: hasUnseenChangelog,
    markAllSeen: markChangelogSeen,
  } = useChangelog(user?.role === 'coach' ? user?.id : null);
  const [isChangelogOpen, setIsChangelogOpen] = useState(false);
  // Figée à l'ouverture : markAllSeen fait disparaître la pastille tout de
  // suite, mais les entrées non vues restent mises en avant tant que le volet
  // est ouvert.
  const [changelogSeenBeforeOpen, setChangelogSeenBeforeOpen] = useState(null);

  const openChangelog = useCallback(() => {
    setChangelogSeenBeforeOpen(changelogLastSeenDate);
    setIsChangelogOpen(true);
    markChangelogSeen();
  }, [changelogLastSeenDate, markChangelogSeen]);

  // Pop-up « Nouveautés » : une fois par coach et par mise à jour mise en avant
  // (retenu sur l'appareil), pas pour un coach inscrit après celle-ci.
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const whatsNewKey = user?.id ? `kaiylo_whatsnew_seen_${user.id}` : null;
  useEffect(() => {
    if (user?.role !== 'coach' || !whatsNewKey) return;
    if (isWhatsNewDue(safeGetItem(whatsNewKey), user.createdAt, CHANGELOG_SPOTLIGHT_DATE)) setShowWhatsNew(true);
  }, [user?.role, user?.createdAt, whatsNewKey]);
  const closeWhatsNew = useCallback(() => {
    if (whatsNewKey) safeSetItem(whatsNewKey, CHANGELOG_SPOTLIGHT_DATE);
    setShowWhatsNew(false);
  }, [whatsNewKey]);

  const openNotifications = useCallback(() => setIsNotificationOpen(true), []);
  const openMenu = useCallback(() => setIsMobileMenuOpen(true), []);

  // Check if any modal is open by looking for backdrop elements in the DOM
  // This works for all modals, not just those registered in ModalManager
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  useEffect(() => {
    const checkForModal = () => {
      // Check for elements with fixed inset-0 backdrop-blur (typical modal backdrop)
      const backdrops = document.querySelectorAll('[class*="fixed"][class*="inset-0"][class*="backdrop-blur"]');
      const hasModal = backdrops.length > 0;
      setIsModalOpen(hasModal);
    };
    
    // Check initially
    checkForModal();
    
    // Use MutationObserver to watch for DOM changes (modals opening/closing)
    const observer = new MutationObserver(checkForModal);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
    
    return () => observer.disconnect();
  }, []);

  // Fetch coach plan to show/hide upgrade button
  const fetchCoachPlan = useCallback(async () => {
    if (user?.role !== 'coach') {
      setIsCoachPlanLoaded(true);
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setCoachPlan('free');
        return;
      }
      const res = await axios.get(buildApiUrl('/api/billing/status'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setCoachPlan(res.data.data?.plan || 'free');
      }
    } catch {
      // silently fail — default stays 'free'
    } finally {
      setIsCoachPlanLoaded(true);
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role === 'coach') setIsCoachPlanLoaded(false);
    fetchCoachPlan();
  }, [user?.id, user?.role, fetchCoachPlan]);

  // The plan can change from elsewhere in the app (billing page, client-limit modal).
  // Without this the header keeps offering the plan the coach already left, until a
  // full page reload. One listener, refetch only when a change actually happened.
  useEffect(() => {
    const onPlanChanged = () => { fetchCoachPlan(); };
    window.addEventListener('kaiylo:plan-changed', onPlanChanged);
    return () => window.removeEventListener('kaiylo:plan-changed', onPlanChanged);
  }, [fetchCoachPlan]);

  // Load notifications from server on mount (for both coaches and students)
  useEffect(() => {
    if (!user?.id) return;

    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const apiUrl = `${getApiBaseUrlWithApi()}/notifications`;
        const response = await axios.get(
          apiUrl,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            params: {
              limit: 50
            }
          }
        );

        if (response.data.success && Array.isArray(response.data.data)) {
          // Transform database notifications to the format expected by the UI
          const transformedNotifications = response.data.data.map((notif) => {
            // Different format for coaches (video_upload) vs students (video_feedback)
            if (notif.type === 'video_upload') {
              // Coach notification (student uploaded a video)
              return {
                id: notif.id,
                type: 'video_upload',
                studentName: notif.data?.studentName || 'Client',
                exerciseName: notif.data?.exerciseName || '',
                setInfo: notif.data?.setInfo || null,
                sessionName: notif.data?.sessionName || null,
                timestamp: notif.created_at || notif.data?.timestamp || new Date().toISOString(),
                read: notif.read || false,
                videoId: notif.data?.videoId,
                // Include full data object for setNumber and setIndex (totalSets)
                data: notif.data || {},
                // Include message and title for parsing the format
                message: notif.message || '',
                title: notif.title || ''
              };
            } else if (notif.type === 'video_feedback') {
              // Student notification (coach sent feedback)
              return {
                id: notif.id,
                type: 'video_feedback',
                coachName: notif.data?.coachName || 'Coach',
                exerciseName: notif.data?.exerciseName || '',
                sessionName: notif.data?.sessionName || null,
                setInfo: notif.data?.setInfo || null,
                feedback: notif.data?.feedback || '',
                rating: notif.data?.rating || null,
                timestamp: notif.created_at || notif.data?.timestamp || new Date().toISOString(),
                read: notif.read || false,
                videoId: notif.data?.videoId,
                // Include full data object for setNumber and setIndex (totalSets)
                data: notif.data || {},
                // Include message and title for parsing the format
                message: notif.message || '',
                title: notif.title || ''
              };
            } else if (notif.type === 'access_change') {
              // Student notification (access downgraded or restored by coach)
              return {
                id: notif.id,
                type: 'access_change',
                title: notif.title || '',
                message: notif.message || '',
                event: notif.data?.event || '',
                timestamp: notif.created_at || new Date().toISOString(),
                read: notif.read || false,
                data: notif.data || {}
              };
            } else if (notif.type === 'session_assigned' || notif.type === 'league_change' || notif.type === 'questionnaire_due' || notif.type === 'match_plan_shared') {
              // Student notification (coach assigned a session / league promotion or relegation).
              // Same minimal shape as access_change: the sheet builds its copy from `data` via i18n.
              return {
                id: notif.id,
                type: notif.type,
                title: notif.title || '',
                message: notif.message || '',
                timestamp: notif.created_at || new Date().toISOString(),
                read: notif.read || false,
                data: notif.data || {}
              };
            } else if (notif.type === 'set_corrected' || notif.type === 'questionnaire_submitted' || notif.type === 'candidature_received') {
              // Coach notification (student corrected a failed set / answered a
              // questionnaire, or someone applied through the candidature page).
              return {
                id: notif.id,
                type: notif.type,
                title: notif.title || '',
                message: notif.message || '',
                studentName: notif.data?.studentName || notif.data?.prospectName || '',
                timestamp: notif.created_at || new Date().toISOString(),
                read: notif.read || false,
                data: notif.data || {}
              };
            }
            return null;
          }).filter(Boolean);

          setNotifications(transformedNotifications);
        }
      } catch (error) {
        logger.error('❌ Error loading notifications:', error.message);
      }
    };

    fetchNotifications();
  }, [user?.role, user?.id]);

  // Listen for video upload events (coach side)
  useEffect(() => {
    if (user?.role !== 'coach' || !onVideoUpload) return;

    const unsubscribe = onVideoUpload((payload) => {
      setNotifications((prev) => {
        // Extract notification data from payload (could be in payload.notification or payload directly)
        const notification = payload?.notification || payload;
        const newNotification = {
          id: notification?.id || payload?.notificationId || payload?.id || payload?.videoId || `${Date.now()}-${Math.random()}`,
          type: 'video_upload',
          studentName: notification?.data?.studentName || payload?.studentName || payload?.student?.name || payload?.studentEmail || 'Client',
          exerciseName: notification?.data?.exerciseName || payload?.exerciseName || payload?.exercise?.name || '',
          setInfo: notification?.data?.setInfo || payload?.setInfo,
          sessionName: notification?.data?.sessionName || payload?.sessionName || null,
          timestamp: notification?.created_at || payload?.timestamp || new Date().toISOString(),
          read: false,
          videoId: notification?.data?.videoId || payload?.videoId,
          // Include full data object for setNumber and setIndex (totalSets)
          data: {
            ...(notification?.data || {}),
            setNumber: payload?.setNumber || notification?.data?.setNumber,
            setIndex: payload?.totalSets || notification?.data?.setIndex || notification?.data?.totalSets
          },
          // Include message and title for parsing the format
          message: notification?.message || '',
          title: notification?.title || ''
        };

        // Check if this notification already exists (avoid duplicates)
        const exists = prev.some(n => n.videoId === newNotification.videoId && n.type === 'video_upload');
        if (exists) {
          return prev;
        }

        return [newNotification, ...prev].slice(0, 50); // cap to 50
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.role, onVideoUpload]);

  // Listen for feedback events (student side)
  useEffect(() => {
    if (user?.role !== 'student' || !onFeedback) return;

    const unsubscribe = onFeedback((payload) => {
      setNotifications((prev) => {
        // Extract notification data from payload (could be in payload.notification or payload directly)
        const notification = payload?.notification || payload;
        const newNotification = {
          id: notification?.id || payload?.notificationId || payload?.id || payload?.videoId || `${Date.now()}-${Math.random()}`,
          type: 'video_feedback',
          coachName: notification?.data?.coachName || payload?.coachName || 'Coach',
          exerciseName: notification?.data?.exerciseName || payload?.exerciseName || '',
          sessionName: notification?.data?.sessionName || payload?.sessionName || null,
          setInfo: notification?.data?.setInfo || payload?.setInfo,
          feedback: notification?.data?.feedback || payload?.feedback || '',
          rating: notification?.data?.rating || payload?.rating || null,
          timestamp: notification?.created_at || payload?.timestamp || new Date().toISOString(),
          read: false,
          videoId: notification?.data?.videoId || payload?.videoId,
          // Include full data object for setNumber and setIndex (totalSets)
          data: {
            ...(notification?.data || {}),
            setNumber: payload?.setNumber || notification?.data?.setNumber,
            setIndex: payload?.totalSets || notification?.data?.setIndex || notification?.data?.totalSets
          },
          // Include message and title for parsing the format
          message: notification?.message || '',
          title: notification?.title || ''
        };

        // Check if this notification already exists (avoid duplicates)
        const exists = prev.some(n => n.videoId === newNotification.videoId && n.type === 'video_feedback');
        if (exists) {
          return prev;
        }

        return [newNotification, ...prev].slice(0, 50); // cap to 50
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.role, onFeedback]);

  // Listen for access change events (student side)
  useEffect(() => {
    if (user?.role !== 'student' || !onAccessChange) return;

    const unsubscribe = onAccessChange((payload) => {
      setNotifications((prev) => {
        const newNotification = {
          id: payload?.id || `${Date.now()}-${Math.random()}`,
          type: 'access_change',
          title: payload?.title || '',
          message: payload?.message || '',
          event: payload?.data?.event || '',
          timestamp: payload?.created_at || new Date().toISOString(),
          read: false,
          data: payload?.data || {}
        };

        // Avoid duplicates by ID
        const exists = prev.some(n => n.id === newNotification.id && n.type === 'access_change');
        if (exists) return prev;

        return [newNotification, ...prev].slice(0, 50);
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.role, onAccessChange]);

  // Session assigned / league change (student side) — the payload is the DB row.
  useEffect(() => {
    if (user?.role !== 'student' || !onStudentNotification) return;

    const unsubscribe = onStudentNotification((payload) => {
      if (!payload?.id) return;
      setNotifications((prev) => {
        if (prev.some((n) => n.id === payload.id)) return prev;
        return [{
          id: payload.id,
          type: payload.type,
          title: payload.title || '',
          message: payload.message || '',
          timestamp: payload.created_at || new Date().toISOString(),
          read: false,
          data: payload.data || {}
        }, ...prev].slice(0, 50);
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.role, onStudentNotification]);

  // Série corrigée, questionnaire rempli… (coach side) — payload.notification is the DB row.
  useEffect(() => {
    if (user?.role !== 'coach' || !onCoachNotification) return;

    const unsubscribe = onCoachNotification((payload) => {
      const row = payload?.notification;
      if (!row?.id) return;
      setNotifications((prev) => {
        if (prev.some((n) => n.id === row.id)) return prev;
        return [{
          id: row.id,
          type: row.type || 'set_corrected',
          title: row.title || '',
          message: row.message || '',
          studentName: row.data?.studentName || row.data?.prospectName || '',
          timestamp: row.created_at || new Date().toISOString(),
          read: false,
          data: row.data || {}
        }, ...prev].slice(0, 50);
      });
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user?.role, onCoachNotification]);

  // Mot à recopier pour confirmer (« SUPPRIMER » / « DELETE »), insensible à la casse.
  const deleteConfirmWord = t('header.delete_account_confirm_word');
  const canDeleteAccount = !isDeletingAccount
    && deleteConfirmText.trim().toLocaleUpperCase() === deleteConfirmWord.toLocaleUpperCase();

  const handleDeleteAccount = async () => {
    if (!canDeleteAccount) return;
    setIsDeletingAccount(true);
    try {
      const token = localStorage.getItem('authToken');
      await axios.delete(buildApiUrl('/auth/me'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Déconnexion choisie par l'utilisateur : on désabonne aussi cet appareil du push.
      logout(false, '/login', { unsubscribePush: true });
      navigate('/login', { replace: true });
    } catch (err) {
      // La modale se fermait sans rien dire : l'utilisateur cliquait, rien ne se
      // passait, et il n'avait aucun moyen de savoir que le serveur avait refusé.
      // On garde la modale ouverte et on affiche la raison.
      logger.error('Account deletion error:', err);
      setDeleteAccountError(
        err?.response?.data?.reason
        || err?.response?.data?.message
        || t('header.delete_account_error')
      );
      setIsDeletingAccount(false);
    }
  };

  // Determine page title based on current route
  const getPageTitle = () => {
    if (location.pathname.includes('/exercises')) {
      return t('nav.exercises');
    }
    if (location.pathname.includes('/videotheque') || location.pathname.includes('/videos')) {
      return t('nav.video_library');
    }
    if (location.pathname.includes('/chat')) {
      return t('nav.messages');
    }
    if (location.pathname.includes('/coach/facturation')) {
      return t('nav.billing');
    }
    if (location.pathname.includes('/coach/templates')) {
      return t('nav.templates');
    }
    if (location.pathname.includes('/coach/questionnaires')) {
      return t('nav.questionnaires');
    }
    if (location.pathname.includes('/coach/dashboard')) {
      return t('nav.clients');
    }
    if (location.pathname.includes('/coach/rankings')) {
      return t('nav.rankings');
    }
    if (location.pathname.includes('/coach/competitions')) {
      return t('nav.competitions');
    }
    if (location.pathname.startsWith('/admin/')) {
      return 'Pilotage'; // espace de l'équipe, en français seulement
    }
    // Add more route-based titles as needed
    return t('nav.clients');
  };

  const handleNotificationClick = useCallback(async (notif) => {
    // Close notification sidebar
    setIsNotificationOpen(false);

    // Mark as read only if the notification has a valid database ID
    // WebSocket notifications might have a different ID structure
    if (!notif.read && notif.id && typeof notif.id === 'string' && notif.id.length === 36) {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        await axios.patch(
          `${getApiBaseUrlWithApi()}/notifications/${notif.id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update local state
        setNotifications(prev => 
          prev.map(n => n.id === notif.id ? { ...n, read: true } : n)
        );
      } catch (error) {
        // Only log if it's not a 404 (404 means notification doesn't exist in DB, which is OK for WebSocket notifications)
        if (error.response?.status !== 404) {
          logger.error('Error marking notification as read:', error);
        }
        // Still update local state to mark as read visually
        setNotifications(prev => 
          prev.map(n => n.id === notif.id ? { ...n, read: true } : n)
        );
      }
    } else if (!notif.read) {
      // For WebSocket notifications without DB ID, just mark as read locally
      setNotifications(prev => 
        prev.map(n => n.id === notif.id ? { ...n, read: true } : n)
      );
    }

    if (notif.type === 'session_assigned') {
      const date = notif.data?.firstDueDate;
      navigate(date ? `/student/dashboard?date=${date}` : '/student/dashboard');
      return;
    }
    if (notif.type === 'league_change') {
      navigate('/student/rankings');
      return;
    }
    if (notif.type === 'match_plan_shared' && notif.data?.entryId) {
      const { entryId, competitionId } = notif.data;
      navigate(`/student/competitions/${entryId}${competitionId ? `?c=${encodeURIComponent(competitionId)}` : ''}`);
      return;
    }
    if (notif.type === 'questionnaire_due' && notif.data?.responseId) {
      navigate(`/student/questionnaire/${notif.data.responseId}`);
      return;
    }
    if (notif.type === 'questionnaire_submitted') {
      const studentId = notif.data?.studentId;
      navigate(studentId ? `/coach/dashboard?studentId=${studentId}&tab=bilans` : '/coach/dashboard');
      return;
    }
    if (notif.type === 'candidature_received') {
      const prospectId = notif.data?.prospectId;
      navigate(prospectId
        ? `/coach/questionnaires?tab=candidature&prospect=${prospectId}`
        : '/coach/questionnaires?tab=candidature');
      return;
    }
    if (notif.type === 'set_corrected') {
      const studentId = notif.data?.studentId;
      navigate(studentId ? `/coach/dashboard?studentId=${studentId}` : '/coach/dashboard');
      return;
    }

    // Open video if it's a video notification
    if (notif.videoId) {
      // Student: the library's own detail screen, hosted full-screen. It
      // fetches the full row itself, so no prefetch here.
      if (user?.role === 'student') {
        setSelectedVideo({ id: notif.videoId });
        setIsVideoModalOpen(true);
        return;
      }
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        // Fetch video details
        const response = await axios.get(
          `${getApiBaseUrlWithApi()}/videos/${notif.videoId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success && response.data.video) {
          setSelectedVideo(response.data.video);
          setIsVideoModalOpen(true);
        }
      } catch (error) {
        logger.error('Error opening video:', error);
        if (error.response?.status === 404) {
          alert(t('header.video_not_found_alert'));
        } else {
          alert(t('header.video_load_error_alert'));
        }
      }
    }
  }, [t, user?.role, navigate]);

  const handleMarkAllAsRead = useCallback(async () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (unreadNotifications.length === 0) return;

    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      // Try to mark all as read via API (if endpoint exists)
      // First, try a bulk endpoint
      try {
        await axios.patch(
          `${getApiBaseUrlWithApi()}/notifications/read-all`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (bulkError) {
        // If bulk endpoint doesn't exist, mark each notification individually
        if (bulkError.response?.status === 404 || bulkError.response?.status === 405) {
          // Mark each notification with a valid DB ID
          const validNotifications = unreadNotifications.filter(
            n => n.id && typeof n.id === 'string' && n.id.length === 36
          );
          
          await Promise.allSettled(
            validNotifications.map(notif =>
              axios.patch(
                `${getApiBaseUrlWithApi()}/notifications/${notif.id}/read`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              )
            )
          );
        } else {
          throw bulkError;
        }
      }

      // Update local state - mark all as read
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
      // Still update local state to mark as read visually
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
    }
  }, [notifications]);

  const handleToggleNotificationRead = useCallback(async (notif) => {
    const newReadState = !notif.read;
    
    // Update local state immediately for better UX
    setNotifications(prev => 
      prev.map(n => n.id === notif.id ? { ...n, read: newReadState } : n)
    );

    // If notification has a valid database ID, update on server
    if (notif.id && typeof notif.id === 'string' && notif.id.length === 36) {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        if (newReadState) {
          // Mark as read
          await axios.patch(
            `${getApiBaseUrlWithApi()}/notifications/${notif.id}/read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } else {
          // Mark as unread - try unread endpoint, fallback to read with false
          try {
            await axios.patch(
              `${getApiBaseUrlWithApi()}/notifications/${notif.id}/unread`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (unreadError) {
            // If unread endpoint doesn't exist, try alternative approach
            if (unreadError.response?.status === 404 || unreadError.response?.status === 405) {
              // Some APIs might accept a body with read: false
              await axios.patch(
                `${getApiBaseUrlWithApi()}/notifications/${notif.id}/read`,
                { read: false },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } else {
              throw unreadError;
            }
          }
        }
      } catch (error) {
        // Revert local state on error
        setNotifications(prev => 
          prev.map(n => n.id === notif.id ? { ...n, read: !newReadState } : n)
        );
        logger.error('Error toggling notification read state:', error);
      }
    }
  }, []);

  // Publish the overlay openers AND the notification slice, so a page that
  // renders its own chrome (the student dashboard) can both open this Header's
  // sidebar and render the same notifications in its own bottom sheet — from
  // this one fetch, with these one set of handlers. Declared after the handlers
  // because it closes over them.
  // The coach mobile header and profile sheet (MainLayout) also reach the
  // changelog and the account-deletion modal through here: this Header stays
  // their single owner, the bar that used to open them is hidden on phones.
  const openDeleteAccount = useCallback(() => {
    setDeleteAccountError(null);
    setIsDeleteAccountModalOpen(true);
  }, []);

  const headerActions = useMemo(() => ({
    openNotifications,
    openMenu,
    openChangelog,
    openDeleteAccount,
    hasUnseenChangelog,
    notifications,
    onNotificationClick: handleNotificationClick,
    onMarkAllAsRead: handleMarkAllAsRead,
    onToggleRead: handleToggleNotificationRead
  }), [
    openNotifications,
    openMenu,
    openChangelog,
    openDeleteAccount,
    hasUnseenChangelog,
    notifications,
    handleNotificationClick,
    handleMarkAllAsRead,
    handleToggleNotificationRead
  ]);

  useEffect(() => {
    if (!registerActions) return undefined;
    registerActions(headerActions);
    return undefined;
  }, [registerActions, headerActions]);

  // De-registration is a separate effect keyed on `registerActions` alone, so a
  // new notification re-registers WITHOUT first publishing the empty fallback.
  // Folding it into the effect above would push subscribers through a transient
  // "no notifications" value on every socket event.
  useEffect(() => {
    if (!registerActions) return undefined;
    return () => registerActions(null);
  }, [registerActions]);

  const handlePortalSession = useCallback(async () => {
    try {
      setCheckoutLoading(true);
      const token = await getAuthToken();
      const response = await fetch(buildApiUrl('/api/billing/create-portal-session'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.success && result.data?.portalUrl) {
        window.location.href = result.data.portalUrl;
      }
    } catch (err) {
      logger.error('Portal request failed:', err);
    } finally {
      setCheckoutLoading(false);
    }
  }, [getAuthToken]);

  const handleCheckout = useCallback(async (affiliationCode) => {
    try {
      setCheckoutLoading(true);
      const token = await getAuthToken();
      const response = await fetch(buildApiUrl('/api/billing/create-checkout-session'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          planName: selectedUpgradePlan?.name || 'starter',
          ...(affiliationCode ? { affiliationCode } : {})
        })
      });
      const result = await response.json();
      if (result.success && result.data?.checkoutUrl) {
        window.location.href = result.data.checkoutUrl;
      } else {
        logger.error('Checkout session error:', result);
        setShowUpgradeModal(false);
      }
    } catch (err) {
      logger.error('Checkout request failed:', err);
      setShowUpgradeModal(false);
    } finally {
      setCheckoutLoading(false);
    }
  }, [getAuthToken]);

  const renderCoachHeader = () => (
    <div className="flex items-center w-full pt-2 pb-[18px] border-b border-white/10">
      {/* Left side - Title and Hamburger menu */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Hamburger menu (mobile only) */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden text-muted-foreground hover:text-foreground h-9 w-9 flex-shrink-0"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label={t('header.open_menu')}
        >
          <Menu style={{ width: '18px', height: '18px', color: 'rgba(255, 255, 255, 0.7)' }} />
        </Button>
        
        {/* Page Title */}
        <h1 className="text-[32px] text-white leading-[0] not-italic whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 200, color: 'rgba(255, 255, 255, 1)' }}>
          <span className="leading-[normal]" style={{ color: 'rgba(255, 255, 255, 1)' }}>{getPageTitle()}</span>
        </h1>
      </div>

      {/* Right side - Action buttons */}
      <div className="flex items-center gap-0 flex-shrink-0">
        {(() => {
          // Upsell button removed on request: changing plan belongs on the
          // Facturation page, where the prorated amount is shown before charging.
          // Kept behind an early return rather than deleted, so it can come back.
          return null;
          /* eslint-disable no-unreachable */
          if (!isCoachPlanLoaded) return null;
          const PLANS = ['free', 'starter', 'growth', 'scale'];
          const PLAN_LABELS = { starter: 'Starter', growth: 'Growth', scale: 'Scale' };
          const currentIndex = PLANS.indexOf(coachPlan);
          const nextPlan = PLANS[currentIndex + 1];
          if (!nextPlan) return null;
          const PLAN_DETAILS = {
            starter: { name: 'starter', label: 'Starter', price: 29, studentLimit: 10 },
            growth:  { name: 'growth',  label: 'Growth',  price: 49, studentLimit: 20 },
            scale:   { name: 'scale',   label: 'Scale',   price: 69, studentLimit: null }, // unlimited
          };
          const handleClick = coachPlan === 'free'
            ? () => { setSelectedUpgradePlan(PLAN_DETAILS[nextPlan]); setShowUpgradeModal(true); }
            : () => handlePortalSession();
          return (
            <Button
              type="button"
              aria-label={t('header.upgrade_to', { plan: PLAN_LABELS[nextPlan] })}
              className="h-[38px] min-h-[38px] py-0 px-6 mr-2 rounded-lg text-sm font-normal text-white bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 border-0 gap-1.5 max-md:h-8 max-md:min-h-8 max-md:px-2.5 max-md:mr-1 max-md:gap-1 max-md:text-[11px] max-md:max-w-[min(100%,9.5rem)] max-md:min-w-0 max-md:shrink"
              onClick={handleClick}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="h-3 w-3 mr-1 max-md:h-2.5 max-md:w-2.5 max-md:mr-0.5 max-md:flex-shrink-0" fill="currentColor" aria-hidden="true">
                <path d="M338.8-9.9c11.9 8.6 16.3 24.2 10.9 37.8L271.3 224 416 224c13.5 0 25.5 8.4 30.1 21.1s.7 26.9-9.6 35.5l-288 240c-11.3 9.4-27.4 9.9-39.3 1.3s-16.3-24.2-10.9-37.8L176.7 288 32 288c-13.5 0-25.5-8.4-30.1-21.1s-.7-26.9 9.6-35.5l288-240c11.3-9.4 27.4-9.9 39.3-1.3z" />
              </svg>
              <span className="min-w-0 truncate md:whitespace-normal">
                <span className="md:hidden">{PLAN_LABELS[nextPlan]}</span>
                <span className="hidden md:inline">{t('header.upgrade_to', { plan: PLAN_LABELS[nextPlan] })}</span>
              </span>
            </Button>
          );
          /* eslint-enable no-unreachable */
        })()}
        {/* Settings icon → volet « Réglages » (comme Nouveautés et Notifications) */}
        <Button
          variant="ghost"
          size="icon"
          className="text-white/75 hover:text-[#d4845a] h-9 w-9"
          style={{ background: 'unset', backgroundColor: 'unset' }}
          data-testid="header-settings"
          aria-label={t('settings.title')}
          title={t('settings.title')}
          onClick={() => { setHasOpenedSettings(true); setIsSettingsOpen(true); }}
        >
          <SettingsIcon style={{ width: '24px', height: '24px' }} />
        </Button>

        {/* Nouveautés icon */}
        <Button
          variant="ghost"
          size="icon"
          className="relative text-white/75 hover:text-[#d4845a]"
          onClick={openChangelog}
          style={{ background: 'unset', backgroundColor: 'unset' }}
          aria-label={t('changelog.open_aria')}
          title={t('changelog.title')}
        >
          <GiftIcon style={{ width: '21px', height: '21px' }} />
          {hasUnseenChangelog && (
            <span data-testid="changelog-unseen-dot" className="absolute top-1 right-1 h-2 w-2 rounded-full bg-[#d4845a]" />
          )}
        </Button>

        {/* Notification icon */}
        <Button
          variant="ghost"
          size="icon"
          className="relative text-white/75 hover:text-[#d4845a]"
          onClick={() => setIsNotificationOpen(true)}
          style={{ background: 'unset', backgroundColor: 'unset' }}
        >
          <NotificationIcon style={{ width: '24px', height: '24px' }} />
          {notifications.filter(n => !n.read).length > 0 && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-[#d4845a]" />
          )}
        </Button>
      </div>
    </div>
  );

  const renderStudentHeader = () => (
    <div className="flex items-center justify-between gap-3 w-full">
      {/* Pas de hamburger : les élèves n'ont plus de menu latéral — la nav du
          bas et la feuille profil couvrent tout ce qu'il proposait. */}
      {/* Barre de recherche masquée sur le Classement (aucune recherche sur cette page) */}
      {location.pathname.includes('/student/rankings') ? (
        <div className="flex-1" />
      ) : (
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ width: '18px', height: '18px', color: 'rgba(255, 255, 255, 0.7)' }} />
          <Input
            type="search"
            placeholder={t('header.search_placeholder')}
            className="pl-10 border-none placeholder:text-muted-foreground rounded-[99px] font-light"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
          />
        </div>
      )}
      {/* Notification icon */}
      <Button 
        variant="ghost" 
        size="icon" 
        className="relative text-white/75 hover:text-white hover:bg-white/10"
        onClick={() => setIsNotificationOpen(true)}
      >
        <Bell className="h-5 w-5" style={{ color: 'rgba(255, 255, 255, 0.75)' }} />
        {notifications.filter(n => !n.read).length > 0 && (
          <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-[#d4845a]" />
        )}
      </Button>
    </div>
  );

  // In a mobile view (e.g., md breakpoint in tailwind), the header is not shown, 
  // because the MainLayout hides it. This Header is for desktop.
  // The student mobile header is part of the StudentDashboard.
  // So we only need to care about coach desktop and student desktop.
  // Wait, the logic is that the sidebar is hidden on mobile, so the header is still visible.
  // The bottom nav bar appears on mobile. The header should adapt.

  // Let's check MainLayout again.
  // Header is inside <main>. Navigation is outside.
  // On mobile (hidden md:flex for Navigation), the header should still be there.
  // So, the header needs to be responsive.

  const isStudent = user?.role === 'student';

  return (
    <>
      {!hideBar && (
        <header
          className={`relative px-4 sm:px-6 pt-3 pb-0 ${isModalOpen ? 'z-[1]' : 'z-20'}`}
          style={isModalOpen ? { pointerEvents: 'none' } : {}}
        >
          {isStudent ? renderStudentHeader() : renderCoachHeader()}
        </header>
      )}
      {/* Pas de drawer pour les élèves : plus aucun bouton ne l'ouvre côté
          élève, et tout son contenu est accessible via la nav du bas et la
          feuille profil. */}
      {!isStudent && (
        <MobileNavigationDrawer
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          onOpenNotificationSettings={openPushSettings}
        />
      )}
      {/* Rendered after the drawer: when both change in one commit, the sheet's
          scroll lock is applied after the drawer releases its own. */}
      {hasOpenedPushSettings && (
        <React.Suspense fallback={null}>
          <NotificationSettingsSheet
            isOpen={isPushSettingsOpen}
            onClose={closePushSettings}
            role={user?.role}
          />
        </React.Suspense>
      )}
      <NotificationSidebar 
        isOpen={isNotificationOpen} 
        onClose={() => setIsNotificationOpen(false)} 
        onNotificationClick={handleNotificationClick}
        onMarkAllAsRead={handleMarkAllAsRead}
        onToggleRead={handleToggleNotificationRead}
        role={user?.role}
        notifications={notifications}
      />
      {/* Mounted on first open, then kept so its slide-out plays. */}
      {!isStudent && hasOpenedSettings && (
        <React.Suspense fallback={null}>
          <SettingsSidebar
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
            onDeleteAccount={() => {
              setIsSettingsOpen(false);
              setDeleteAccountError(null);
              setDeleteConfirmText('');
              setIsDeleteAccountModalOpen(true);
            }}
          />
        </React.Suspense>
      )}
      {!isStudent && showWhatsNew && (
        <React.Suspense fallback={null}>
          <WhatsNewModal
            onClose={closeWhatsNew}
            onSeeAll={openChangelog}
            onNavigate={(path) => navigate(path)}
          />
        </React.Suspense>
      )}
      {!isStudent && (
        <ChangelogSidebar
          isOpen={isChangelogOpen}
          onClose={() => setIsChangelogOpen(false)}
          entries={changelogEntries}
          lastSeenDate={changelogSeenBeforeOpen}
          onNavigate={(path) => {
            setIsChangelogOpen(false);
            navigate(path);
          }}
        />
      )}
      {isVideoModalOpen && (
        <React.Suspense fallback={null}>
          {isStudent ? (
            selectedVideo?.id && (
              <StudentVideoDetailOverlay
                videoId={selectedVideo.id}
                onBack={() => {
                  setIsVideoModalOpen(false);
                  setSelectedVideo(null);
                }}
              />
            )
          ) : (
            <VideoDetailModal
              isOpen={isVideoModalOpen}
              onClose={() => {
                setIsVideoModalOpen(false);
                setSelectedVideo(null);
              }}
              video={selectedVideo}
              videoType="student"
              isCoachView={true}
              onFeedbackUpdate={() => {
                // Ideally, refresh notifications or list if needed
                // For now, no action needed here as Header doesn't manage the list
              }}
            />
          )}
        </React.Suspense>
      )}
      {!isStudent && (
        <UpgradeConfirmationModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onConfirm={handleCheckout}
          isLoading={checkoutLoading}
          plan={selectedUpgradePlan}
        />
      )}
      {/* Delete Account Confirmation Modal */}
      <SheetOrModal
        // Même niveau que sur ordinateur (au-dessus de tout).
        sheetZ="z-[10001]"
        isOpen={isDeleteAccountModalOpen}
        onClose={() => {
          if (!isDeletingAccount) {
            setIsDeleteAccountModalOpen(false);
          }
        }}
        modalId="delete-account-modal"
        // Above the settings side panel (9999) that slides out as it opens.
        zIndex={10001}
        closeOnEsc={!isDeletingAccount}
        closeOnBackdrop={!isDeletingAccount}
        size="sm"
        title={
          <span className="flex items-center gap-2 text-[rgba(220,50,50,1)]">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M256 0c14.7 0 28.2 8.1 35.2 21l216 400c6.7 12.4 6.4 27.4-.8 39.5S486.1 480 472 480L40 480c-14.1 0-27.2-7.4-34.4-19.5s-7.5-27.1-.8-39.5l216-400c7-12.9 20.5-21 35.2-21zm0 352a32 32 0 1 0 0 64 32 32 0 1 0 0-64zm0-192c-18.2 0-32.7 15.5-31.4 33.7l7.4 104c.9 12.5 11.4 22.3 23.9 22.3 12.6 0 23-9.7 23.9-22.3l7.4-104c1.3-18.2-13.1-33.7-31.4-33.7z" />
            </svg>
            {t('settings.delete_account')}
          </span>
        }
        titleClassName="text-xl font-normal text-white"
      >
        <div className="space-y-6">
          <p className="text-sm font-extralight text-white/70 leading-relaxed">
            {t('header.delete_account_warning')}
          </p>
          {/* Saisie du mot de confirmation : un clic de travers ne suffit
              plus à effacer un compte (et, pour un coach, ses élèves). */}
          <div className="space-y-2">
            <label htmlFor="delete-account-confirm-input" className="block text-sm font-extralight text-white/70">
              <Trans
                i18nKey="header.delete_account_type_label"
                values={{ word: deleteConfirmWord }}
                components={{ bold: <span className="font-normal text-white" /> }}
              />
            </label>
            <input
              id="delete-account-confirm-input"
              data-testid="delete-account-confirm-input"
              type="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && canDeleteAccount) handleDeleteAccount(); }}
              disabled={isDeletingAccount}
              placeholder={deleteConfirmWord}
              className="w-full rounded-[10px] border-[0.5px] border-white/10 bg-black/40 px-4 py-2.5 text-sm font-light text-white placeholder:text-white/20 outline-none transition-colors focus:border-[rgba(220,50,50,0.6)] disabled:opacity-50"
            />
          </div>
          {deleteAccountError && (
            <div
              role="alert"
              className="rounded-[10px] border-[0.5px] border-[rgba(220,50,50,0.35)] bg-[rgba(220,50,50,0.08)] px-4 py-3 text-sm font-extralight text-white/80"
            >
              {deleteAccountError}
            </div>
          )}
          <div className="flex justify-end gap-3 max-md:gap-2 max-md:[&>button]:h-11 max-md:[&>button]:flex-1 max-md:[&>button]:justify-center max-md:[&>button:first-child:not(:last-child)]:bg-white/5">
            <button
              type="button"
              onClick={() => { setDeleteAccountError(null); setIsDeleteAccountModalOpen(false); }}
              disabled={isDeletingAccount}
              className="px-5 py-2.5 text-sm font-extralight text-white/70 bg-[rgba(0,0,0,0.5)] rounded-[10px] hover:bg-[rgba(255,255,255,0.1)] transition-colors border-[0.5px] border-[rgba(255,255,255,0.05)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('buttons.cancel')}
            </button>
            <button
              type="button"
              onClick={handleDeleteAccount}
              disabled={!canDeleteAccount}
              data-testid="delete-account-confirm-button"
              className="px-5 py-2.5 text-sm font-normal text-white rounded-[10px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: canDeleteAccount ? 'rgba(220, 50, 50, 1)' : 'rgba(220, 50, 50, 0.5)' }}
            >
              {isDeletingAccount ? t('header.deleting') : t('header.delete_account_confirm')}
            </button>
          </div>
        </div>
      </SheetOrModal>
    </>
  );
};

export default Header;
